/**
 * BSF Volume Pricing Component
 * ============================
 * Handles tier selection and updates the theme's quantity input.
 * Uses event delegation for dynamic content (drawers, modals).
 *
 * VERSION: 2026.01.2
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================
  const CONFIG = {
    selectors: {
      component: '[data-bsf-volume-pricing]',
      option: '[data-bsf-option]',
      input: '[data-bsf-qty-input]',
      // Theme's quantity input selectors
      themeQtyInput: 'input[name="quantity"], .m-quantity-input input, [data-quantity-input]',
      // Theme's product form
      productForm: 'product-form form, .m-product-form form, [data-product-form]'
    },
    classes: {
      selected: 'bsf-vp__option--selected'
    }
  };

  // ============================================
  // OPTION SELECTION HANDLER
  // ============================================
  function handleOptionChange(event) {
    const input = event.target;
    if (!input.matches(CONFIG.selectors.input)) return;

    const component = input.closest(CONFIG.selectors.component);
    if (!component) return;

    const option = input.closest(CONFIG.selectors.option);
    if (!option) return;

    // Get selected quantity
    const selectedQty = parseInt(option.dataset.qty, 10);

    // Update selected class on all options
    const allOptions = component.querySelectorAll(CONFIG.selectors.option);
    allOptions.forEach(opt => opt.classList.remove(CONFIG.classes.selected));
    option.classList.add(CONFIG.classes.selected);

    // Find and update the theme's quantity input
    updateThemeQuantityInput(component, selectedQty);

    // Emit custom event for any external listeners
    component.dispatchEvent(new CustomEvent('bsf:option-changed', {
      bubbles: true,
      detail: {
        qty: selectedQty,
        discount: parseInt(option.dataset.discount, 10) || 0,
        totalPrice: parseInt(option.dataset.totalPrice, 10),
        variantId: component.dataset.variantId
      }
    }));
  }

  // ============================================
  // UPDATE THEME'S QUANTITY INPUT
  // ============================================
  function updateThemeQuantityInput(component, qty) {
    // Store the quantity on the component for later use
    component.dataset.selectedQty = qty;

    // Find the section or main product container
    const section = component.closest('.m-main-product') ||
                    component.closest('.m-main-product--wrapper') ||
                    component.closest('.m-product-form--wrapper') ||
                    component.closest('section') ||
                    component.closest('.product-drawer__content');

    if (!section) {
      console.warn('BSF VP: Could not find parent section');
      return;
    }

    // Find ALL quantity inputs in this section and update them
    const qtyInputs = section.querySelectorAll('input[name="quantity"]');
    qtyInputs.forEach(input => {
      input.value = qty;
      // Trigger events so theme JS picks up the change
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    // Also look for the visible quantity input with class
    const visibleQtyInput = section.querySelector('.m-quantity-input input, [data-quantity-input], .m-product-quantity input');
    if (visibleQtyInput && !visibleQtyInput.matches('input[name="quantity"]')) {
      visibleQtyInput.value = qty;
      visibleQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      visibleQtyInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Find the form and ensure its quantity input is set
    const form = section.querySelector('form[action*="/cart/add"]') ||
                 section.querySelector('form[data-type="add-to-cart-form"]') ||
                 section.querySelector('product-form form');

    if (form) {
      let formQtyInput = form.querySelector('input[name="quantity"]');

      // If no quantity input exists in form, create one
      if (!formQtyInput) {
        formQtyInput = document.createElement('input');
        formQtyInput.type = 'hidden';
        formQtyInput.name = 'quantity';
        form.appendChild(formQtyInput);
      }

      formQtyInput.value = qty;
    }

  }

  // ============================================
  // INTERCEPT FORM SUBMISSION
  // ============================================
  function handleFormSubmit(event) {
    const form = event.target;
    if (!form.matches('form')) return;

    // Check if form is an add-to-cart form
    const isCartForm = form.action?.includes('/cart/add') ||
                       form.dataset.type === 'add-to-cart-form' ||
                       form.closest('product-form');

    if (!isCartForm) return;

    // Find BSF component - could be inside form or nearby in same section
    let component = form.querySelector(CONFIG.selectors.component);

    if (!component) {
      // Look in parent section
      const section = form.closest('.m-main-product') ||
                      form.closest('.m-main-product--wrapper') ||
                      form.closest('section') ||
                      form.closest('.product-drawer__content');

      if (section) {
        component = section.querySelector(CONFIG.selectors.component);
      }
    }

    if (!component) return;

    // Get the selected quantity from the component
    const selectedOption = component.querySelector(`.${CONFIG.classes.selected}`);
    const qty = selectedOption
      ? parseInt(selectedOption.dataset.qty, 10)
      : parseInt(component.dataset.selectedQty, 10) || 1;

    // Ensure quantity input has correct value
    let qtyInput = form.querySelector('input[name="quantity"]');

    if (!qtyInput) {
      // Create one if it doesn't exist
      qtyInput = document.createElement('input');
      qtyInput.type = 'hidden';
      qtyInput.name = 'quantity';
      form.appendChild(qtyInput);
    }

    qtyInput.value = qty;
  }

  // ============================================
  // VARIANT CHANGE LISTENER (for PDP)
  // ============================================
  function handleVariantChange(event) {
    const variant = event.detail?.variant || event.variant;
    if (!variant) return;

    // Find all volume pricing components for this product
    const components = document.querySelectorAll(
      `${CONFIG.selectors.component}[data-product-id="${variant.product_id}"]`
    );

    components.forEach(component => {
      // Update variant ID
      component.dataset.variantId = variant.id;
      // Update base price
      component.dataset.basePrice = variant.price;
      // Recalculate prices for all options
      recalculateOptionPrices(component, variant.price);
    });
  }

  // ============================================
  // RECALCULATE PRICES
  // ============================================
  function recalculateOptionPrices(component, basePrice) {
    if (!basePrice || isNaN(basePrice) || basePrice <= 0) return;

    const options = component.querySelectorAll(CONFIG.selectors.option);

    options.forEach(option => {
      const qty = parseInt(option.dataset.qty, 10);
      const discount = parseInt(option.dataset.discount, 10) || 0;

      const discountMultiplier = (100 - discount) / 100;
      const totalPrice = Math.round(basePrice * qty * discountMultiplier);
      const comparePrice = basePrice * qty;
      const perUnitPrice = Math.round(totalPrice / qty);

      // Update data attributes
      option.dataset.totalPrice = totalPrice;
      option.dataset.comparePrice = comparePrice;
      option.dataset.perUnitPrice = perUnitPrice;

      // Update displayed prices
      const totalEl = option.querySelector('.bsf-vp__total-price');
      if (totalEl) totalEl.textContent = formatMoney(totalPrice);

      const compareEl = option.querySelector('.bsf-vp__compare-price');
      if (compareEl) {
        compareEl.textContent = formatMoney(comparePrice);
        compareEl.style.display = discount > 0 ? '' : 'none';
      }

      // Update both per-unit elements (center and right column)
      option.querySelectorAll('.bsf-vp__per-unit, .bsf-vp__per-unit-right').forEach(perUnitEl => {
        const perText = perUnitEl.dataset.perText || '';
        const unitLabel = perUnitEl.dataset.unitLabel || '';
        perUnitEl.textContent = `${formatMoney(perUnitPrice)} ${perText} ${unitLabel}`.trim();
      });
    });
  }

  // ============================================
  // MONEY FORMATTING
  // ============================================
  function formatMoney(cents, format) {
    if (typeof window.MinimogSettings !== 'undefined' && window.MinimogSettings.money_format) {
      format = format || window.MinimogSettings.money_format;
    } else if (typeof window.Shopify !== 'undefined' && window.Shopify.money_format) {
      format = format || window.Shopify.money_format;
    } else {
      format = format || '{{amount}} kr.';
    }

    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '{{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || '.';
      decimal = decimal || ',';

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)?.[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, '.', ',');
    }

    return formatString.replace(placeholderRegex, value);
  }

  // ============================================
  // HANDLE ADD TO CART CLICK
  // ============================================
  function handleAddToCartClick(event) {
    const button = event.target.closest('button[name="add"], button[type="submit"], .m-add-to-cart');
    if (!button) return;

    const form = button.closest('form');
    if (!form) return;

    // Find BSF component
    let component = form.querySelector(CONFIG.selectors.component);

    if (!component) {
      const section = form.closest('.m-main-product') ||
                      form.closest('.m-main-product--wrapper') ||
                      form.closest('section') ||
                      form.closest('.product-drawer__content');

      if (section) {
        component = section.querySelector(CONFIG.selectors.component);
      }
    }

    if (!component) return;

    // Get selected qty and set it on form before submission
    const selectedOption = component.querySelector(`.${CONFIG.classes.selected}`);
    const qty = selectedOption
      ? parseInt(selectedOption.dataset.qty, 10)
      : parseInt(component.dataset.selectedQty, 10) || 1;

    let qtyInput = form.querySelector('input[name="quantity"]');

    if (!qtyInput) {
      qtyInput = document.createElement('input');
      qtyInput.type = 'hidden';
      qtyInput.name = 'quantity';
      form.appendChild(qtyInput);
    }

    qtyInput.value = qty;
  }

  // ============================================
  // INITIALIZATION
  // ============================================
  function init() {
    // Event delegation for option changes
    document.addEventListener('change', handleOptionChange);

    // Intercept form submissions to ensure correct quantity
    document.addEventListener('submit', handleFormSubmit, true);

    // Intercept add-to-cart button clicks
    document.addEventListener('click', handleAddToCartClick, true);

    // Listen for variant changes
    document.addEventListener('variant:changed', handleVariantChange);

    // Also listen for Minimog's variant event
    if (typeof window.MinimogEvents !== 'undefined') {
      window.MinimogEvents.subscribe('variant-change', handleVariantChange);
    }

    // Set initial quantity for any existing components
    initComponents(document);
  }

  // ============================================
  // THEME EDITOR SUPPORT
  // ============================================
  function initComponents(root) {
    root.querySelectorAll(CONFIG.selectors.component).forEach(component => {
      const selectedOption = component.querySelector(`.${CONFIG.classes.selected}`);
      if (selectedOption) {
        const qty = parseInt(selectedOption.dataset.qty, 10);
        updateThemeQuantityInput(component, qty);
      }
    });
  }

  // ============================================
  // RUN
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize components when sections reload in Shopify theme editor
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      if (event.target) {
        initComponents(event.target);
      }
    });
  }

  // Expose for external use
  window.BSFVolumePricing = {
    formatMoney,
    recalculateOptionPrices,
    updateThemeQuantityInput
  };

})();